import {
  getTriggerPoint,
  NewsletterVariant,
  useFallbackStorageValue,
  useNewsletterApi,
} from "@product/scmp-sdk";
import { useSyncedRef, useToggle } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import qs from "qs";
import { useCallback, useEffect } from "react";

import { sendMirrorCustomEvent } from "shared/components/tracking/mirror";
import { useParsedCookie } from "shared/hooks";
import { accountAtom } from "shared/lib/account/atoms";
import { useRouterReplaceWithSanitizedKeys } from "shared/lib/router";

import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import type { DataMatrix } from "scmp-app/components/newsletter/newsletter-widget-container/types";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { sendGTMTracking } from "scmp-app/components/tracking/google-tag-manager/apis";

import {
  SanitizedUrlKeys,
  SignUpMatrixV1LocalStorageKey,
  SubscriptionComboLocalStorageKey,
} from "./consts";
import { shouldShowNewsletterSignUp } from "./helpers";
import type { NewsletterGASuccessParameter, NewsletterPayload } from "./types";

export const useNewsletterSignUpRedirect = () => {
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);

  const { handleSubscribeNewsletter, isDataReady } = useNewsletterApi();
  const { handleNewsletterSuccessTracking } = useNewsletterTracking();
  const latestHandleSubscribeNewsletter = useSyncedRef(handleSubscribeNewsletter);
  const { asPath, pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();
  const [showPopup, setShowPopup] = useToggle(false);

  const { value: dataMatrix } = useFallbackStorageValue<DataMatrix>(SignUpMatrixV1LocalStorageKey);
  const { value: isCombo } = useFallbackStorageValue<boolean>(SubscriptionComboLocalStorageKey);

  const execute = useCallback(
    async (parsedQueryString: NewsletterPayload, useremail: string) => {
      const response = await latestHandleSubscribeNewsletter.current(
        parsedQueryString.newsletterIds,
        useremail,
      );
      handleNewsletterSuccessTracking({
        eventCategory: dataMatrix?.metrics?.event_category,
        isCombo: isCombo ?? false,
        isHongKong25: dataMatrix?.metrics?.isHongKong25,
        payload: parsedQueryString,
        subscriptions: response.entity?.subscriptions?.map(element => element?.entity) ?? [],
      });

      if (!response.errors?.length) {
        setShowPopup(true);
      }
    },
    [
      dataMatrix?.metrics?.event_category,
      dataMatrix?.metrics?.isHongKong25,
      handleNewsletterSuccessTracking,
      isCombo,
      latestHandleSubscribeNewsletter,
      setShowPopup,
    ],
  );

  const needToShow = shouldShowNewsletterSignUp();

  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as NewsletterPayload;

    if (!needToShow || !parsedQueryString) return;
    if (!user?.uuid) {
      openLoginDialog({
        destination: window.location.href,
        ga4CustomParameter: {
          newsletter_name: parsedQueryString?.newsletterName ?? "",
          trigger_point: "newsletter",
        },
      });

      void replace(
        SanitizedUrlKeys,
        { pathname, query },
        {
          shallow: true,
        },
      );
      return;
    }

    if (!isDataReady) return;
    void execute(parsedQueryString, user.useremail);
  }, [asPath, isDataReady, execute, openLoginDialog, pathname, query, replace, user, needToShow]);
  return {
    setShowPopup,
    shouldShowNewsletterSignUpRedirect: showPopup,
    showPopup,
  };
};

export const useNewsletterTracking = () => {
  const { value: loginType } = useParsedCookie("scmp_user_login_type", raw => raw);

  const handleNewsletterSuccessTracking = useCallback(
    (parameter: NewsletterGASuccessParameter) => {
      parameter.payload.newsletterIds.forEach((id, index) => {
        const data = parameter.subscriptions?.find(element => element?.entityId === id);
        const handleTrackGA4 = () => {
          const sanitizedLoginType = loginType ?? "";
          if (parameter.payload.campaignName) {
            // Send once for acquisition newsletter popup
            const isRequireToSendAcquisitionPopupEvent =
              parameter.payload.variant === NewsletterVariant.AcquisitionNewsletterPopup &&
              index === 0;
            if (isRequireToSendAcquisitionPopupEvent) {
              sendGA4Tracking({
                action: "sys",
                category: "nl",
                customized_parameters: {
                  internal_campaign: parameter.payload.campaignName ?? "",
                  method: sanitizedLoginType.toLowerCase(),
                  newsletter_name: parameter.payload.newsletterName,
                  trigger_point: "ppto",
                },
                subcategory: "sign_up",
              });
            }
            return;
          }
          sendGA4Tracking({
            action: "sys",
            category: "nl",
            customized_parameters: {
              internal_campaign: parameter.payload.campaignName ?? "",
              method: sanitizedLoginType.toLowerCase(),
              newsletter_name: parameter.payload.newsletterName,
              trigger_point: getTriggerPoint(parameter.payload.variant),
            },
            subcategory: "sign_up",
          });
        };
        handleTrackGA4();
        sendGTMTracking({
          event: "newsletter-subscribe",
          "newsletter-id": data?.entityId ?? "",
          "newsletter-name": data?.entityLabel ?? "",
        });
        sendMirrorCustomEvent({
          event_name: "newsletter_sign_up",
          newsletter_subscribe: data?.entityLabel ?? "",
        });
      });
    },
    [loginType],
  );

  return {
    handleNewsletterSuccessTracking,
  };
};
